import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import HttpBackend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import config from "./config/environment";
import { grandfatherLanguageChoice } from "./library/models/language";

/**
 * Romanian is the primary market, so the language has to survive a reload.
 *
 * It did not. `LanguageDetector` was registered and then immediately overruled by an
 * explicit `lng: "en"`, which wins over everything the detector finds -- so the stored
 * choice, the browser's own language and the `?lng=` query were all ignored, and every
 * reload put a Romanian user back into English. Dropping `lng` is the whole fix: the
 * detector's default order already prefers a cached choice, and `changeLanguage` from the
 * header picker writes that cache.
 *
 * `supportedLngs` and `load: "languageOnly"` are what keep the detector honest once it is
 * actually consulted. Translations are fetched over HTTP from `/locales/<lng>/`, and only
 * `ro` and `en` exist there, so without these a browser reporting `ro-RO` -- which is what
 * a Romanian browser reports -- would request `/locales/ro-RO/translation.json`, 404, and
 * only then fall back. `languageOnly` strips the region before the request is made, and
 * `supportedLngs` sends anything else straight to `fallbackLng`.
 *
 * `fallbackLng` is Romanian, and matches `Language.PLATFORM_DEFAULT` on the server. It is
 * reached by a browser that is neither Romanian nor English -- and what such a visitor should
 * see is the platform's own language, which is the same answer the server gives when it has to
 * write to somebody whose row says nothing. One question, one answer, on both sides of the
 * wire: an email and the page it links to must not disagree about what "we have no idea who
 * you are" looks like.
 *
 * It was English until the language preference landed, on the argument that English is the
 * better guess for a visitor who is neither. That is a reasonable thing to believe about a
 * lone visitor and the wrong thing to encode here, because it made the client and the server
 * answer the same question two ways -- and this fallback is nearly unreachable in practice:
 * every clinic domain now supplies its own default, and anyone who has ever picked a language
 * carries an explicit choice. What is left is a visitor to carefull.ro with, say, a German
 * browser, seeing the platform's own language. That is what a platform without a translation
 * for you shows you.
 */
/*
 * Before init, because init is what writes `i18nextLng` -- and this reads it to tell an
 * existing user apart from a new visitor. See `library/models/language.ts`: without it, the
 * clinic default introduced alongside this would flip a Romanian user to English on their next
 * load, which is the same failure the paragraph above is about.
 */
grandfatherLanguageChoice();

i18n
    .use(HttpBackend) // load translations from /public/locales
    .use(LanguageDetector) // stored choice, then browser language
    .use(initReactI18next) // pass i18n to React
    .init({
        supportedLngs: ["ro", "en"],
        load: "languageOnly",
        // `load` decides which file is fetched; this decides what the detector reports and
        // caches. Without it a Romanian browser leaves "ro-RO" in localStorage and in
        // `i18n.language`, and the header's picker -- whose options are "ro" and "en" --
        // matches neither and renders blank.
        detection: {
            convertDetectedLanguage: (lng) => lng.split("-")[0],
        },
        fallbackLng: "ro",
        // Was unconditionally on, which put i18next's whole init and every missing-key
        // warning into the production console.
        debug: config.isDevelopment,
        interpolation: {
            escapeValue: false, // React already escapes
        },
    });

export default i18n;
